import {
  compareEqualConditional,
  compareNotEqualConditional,
  getFieldValue
} from "./enable-conditional-fields";

export const setValue = (
  state,
  values,
  { name, type, text, options, params, action, requestKey, data }
) => {
  if (state.hasOwnProperty(requestKey || name)) {
    if (
      !options?.length ||
      type !== "formTextField" ||
      type !== "modernizedTextField"
    ) {
      values[name] = state[requestKey || name];

      return;
    }

    if (
      options?.length &&
      options.some(
        option =>
          !!state[requestKey || name] &&
          state[requestKey || name] ===
            (option?.value ?? option?.text ?? option)
      )
    ) {
      values[name] = state[requestKey || name];
    }
  }

  if (values.hasOwnProperty(name)) {
    return;
  }

  switch (type) {
    case "formTextField":
      let value;

      if (options?.length) {
        const option = options.find(opt => opt.selected) || options[0];
        if (params?.split("|").includes("TDFieldOptionCountryPhoneNumber")) {
          try {
            const prevValue = state[name] ? JSON.parse(state[name]) : "";
            value =
              prevValue === ""
                ? {
                    countryName: option.name,
                    countryCode: option.value,
                    phoneNumber: text
                  }
                : prevValue;
          } catch (error) {
            value = {
              countryName: options[0].name,
              countryCode: options[0].value
            };
          }
        } else {
          value = option ? option.value ?? option.text ?? option : "";
        }
      } else if (params.includes("TDFieldOptionDatePicker")) {
        value = "OND";
      } else {
        value = text || "";
      }

      // initial value for all form components needs to be non-null in order to avoid
      // PropType warning and warning that unmanaged component changes to managed
      // eslint-disable-next-line no-param-reassign
      values[name] = value || "";

      break;

    case "formTextView":
    case "formDateTimePicker":
      values[name] = text || "";
      break;

    case "formCheckboxContainer":
      if (params.includes("TDFieldOptionIsAgreement") && action) {
        // eslint-disable-next-line no-param-reassign
        values[name] = {
          agreement_id: action.data?.agreement_id,
          accepted: options[0].selected
        };
      } else if (params.includes("TDFieldOptionIsOptional")) {
        options.forEach(option => {
          // eslint-disable-next-line no-param-reassign
          values[option.name] = option.selected ? "YES" : "NO";
        });
      } else if (params.includes("TDFieldOptionReturnArray")) {
        const selectedOptionsArray = options.filter(option => option.selected);
        const newValuesArray = selectedOptionsArray.map(option => option.value);
        values[name] = newValuesArray;
      }

      break;

    case "segmentControlField":
      const option = options.find(({ selected }) => selected);

      if (option && option.value) {
        // eslint-disable-next-line no-param-reassign
        values[`__${name}`] = option.value;
      }

      break;

    case "slider":
      const selectedOptionSlider = options.find(opt => opt.selected);

      if (selectedOptionSlider) {
        // eslint-disable-next-line no-param-reassign
        values[name] = selectedOptionSlider.value;
      }

      break;

    case "formTextLabel":
      if (
        text &&
        (!params.includes("TDFieldOptionIgnore") ||
          !params.includes("TDFieldOptionHasBottomDivider"))
      ) {
        // eslint-disable-next-line no-param-reassign
        values[name] = text;
      }
      break;

    case "cellWithCheck": {
      const selectedOpt = options.find(opt => opt.selected);
      if (selectedOpt) {
        const textName = name.replace("_id", "");
        values[name] = selectedOpt.value;
        if (params.includes("TDFieldOptionIncludesOptions")) {
          values[textName] = selectedOpt.value;
        } else if (selectedOpt.text) {
          values[textName] = selectedOpt.text;
        }
      }
      break;
    }

    case "cellWithCheckMultiple": {
      if (options.length >= 2 && name !== "mhd_answer_ids.234") {
        const textName = name.replace("_id", "");
        const selOpt = options.find(o => o.selected);
        if (selOpt) {
          values[name] = selOpt.value;
          values[textName] = text || "";
        } else if (text) {
          // Family history multioptions do not have a selected
          values[textName] = text;
          values[name] = options.find(o => o.name === "Y").value;
        } else {
          values[textName] = "";
          values[name] = options.find(o => o.name === "N").value;
        }
      }
      break;
    }

    case "cellWithSubtitle": {
      const selectedOptionSubtitle = options?.find(opt => opt.selected);
      if (selectedOptionSubtitle) {
        values[name] = selectedOptionSubtitle.value;
      }
      break;
    }

    case "cellWithSwitch": {
      const selectedOptionSwitch = options?.find(opt => opt.selected);
      if (selectedOptionSwitch) {
        values[name] = selectedOptionSwitch.value;
      } else {
        const noOption = options?.find(opt => opt.name === "N");
        if (noOption) {
          values[name] = noOption.value;
        }
      }
      break;
    }

    case "modernizedToggle": {
      const selectedOptionSwitch = options?.find(opt => opt.selected);
      if (selectedOptionSwitch) {
        values[name] = selectedOptionSwitch.value;
      } else {
        const noOption = options?.find(opt => opt.name === "N");
        if (noOption) {
          values[name] = noOption.value;
        }
      }
      break;
    }

    case "modernizedPhoneNumber": {
      values[name] = text || "";
      break;
    }

    case "suggestionChips": {
      const selectedChipOption = data?.options?.find(opt => opt?.isSelected);
      values[name] = selectedChipOption?.optionValue ?? "";
      break;
    }

    default: {
      break;
    }
  }
};

const updateValues = (state, values, fields) =>
  fields.forEach(field => {
    // TODO: think how it could be refactored properly to handle two text fields in one line
    if (field.fieldLayouts?.length) {
      const list = field.fieldLayouts[0].containerFields;
      updateValues(state, values, list);
    } else {
      setValue(state, values, field);
    }
  });

const extractFieldValues = (screen, state, data) => {
  const values = {
    ...(["Payment", "PaymentCardShorten", "PaymentPaypalShorten"].includes(
      screen.name
    )
      ? {}
      : screen.hiddenFields),
    ...data.hidden_fields
  };
  updateValues(state, values, screen.fields);

  if (screen.footerFields) {
    screen.footerFields.forEach(field => setValue(state, values, field));
  }

  if (screen.conditionals) {
    screen.conditionals.forEach(conditional => {
      const fieldValue = getFieldValue(values[conditional.fieldName]);

      if (conditional.type === "equal") {
        if (compareEqualConditional(fieldValue, conditional.value)) {
          conditional.fields.forEach(field => setValue(state, values, field));
        } else {
          conditional.fields.forEach(field => setValue({}, values, field));
        }
      } else if (conditional.type === "notEqual") {
        if (compareNotEqualConditional(fieldValue, conditional.value)) {
          conditional.fields.forEach(field => setValue(state, values, field));
        } else {
          conditional.fields.forEach(field => setValue({}, values, field));
        }
      }
    });
  }

  return values;
};

export default extractFieldValues;
